<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_ACCOUNT_OFFICER')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="accountOfficer"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="cachedAccountOfficerList ? cachedAccountOfficerList.data : accountOfficerList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_ACCOUNT_OFFICER')"
              @click="onSelectAccountOfficerItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('Modify') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission('UNLINK_ACCOUNT_OFFICER')"
              @click="unlockAccountOfficer(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ $t('Unlink User') }}</span>
            </b-dropdown-item>
            <b-dropdown-item @click="onSelectAccountOfficerItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="
                getUserPermission(
                  data.item.status === 'Active'
                    ? 'DEACTIVATE_ACCOUNT_OFFICER'
                    : 'ACTIVATE_ACCOUNT_OFFICER'
                )
              "
              @click="accountOfficerStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{
                data.item.status === 'Active' ? $t('Deactivate') : $t('Activate')
              }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="accountOfficerRules">
        <b-form>
          <b-row class="match-height">
            <!--  Linked User -->
            <b-col md="6">
              <SelectField
                id="select-user"
                v-model="accountOfficerFormData.linkedUser"
                :label="$t('Select User')"
                :options="userOptions"
                :placeholder="$t('Select')"
                :reduce="option => option.value"
                item="label"
                rules="required"
                :error-name="$t('Select User')"
                :set-selected="setUserDetails"
                :loading="isLoading"
                :readonly="isReadonly"
              />
            </b-col>
            <!--  User Options -->

            <!-- First names -->
            <b-col md="6">
              <InputField
                id="first-name"
                v-model="accountOfficerFormData.otherNames"
                :label="$t('First Names')"
                :placeholder="$t('Enter First names')"
                type="text"
                :error-name="$t('First Names')"
                rules="required"
                for="first-name"
                readonly
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- First names -->

            <!-- Last Name -->
            <b-col md="6">
              <InputField
                id="last-name"
                v-model="accountOfficerFormData.lastName"
                :label="$t('Last Name')"
                :placeholder="$t('Enter last name')"
                type="text"
                :error-name="$t('Last Name')"
                rules="required"
                readonly
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Last Name -->

            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="accountOfficerFormData.branchCode"
                :label="$t('Branch')"
                :options="branchOptions"
                :reduce="option => option.value"
                :error-name="$t('Branch')"
                rules="required"
                item="label"
                readonly
                :placeholder="$t('Select Branch')"
              />
            </b-col>
            <!--Branch -->

            <!--Staff ID -->
            <b-col md="6">
              <InputField
                id="staff-id"
                v-model="accountOfficerFormData.staffID"
                :label="$t('Staff ID')"
                :placeholder="$t('Enter staff ID')"
                type="text"
                :error-name="$t('Staff ID')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Staff ID -->

            <!-- Phone number -->
            <b-col md="6">
              <InputField
                id="phone-number"
                v-model="accountOfficerFormData.phoneNumber"
                :label="$t('Phone Number')"
                :placeholder="$t('Enter phone number')"
                type="number"
                :error-name="$t('Phone Number')"
                rules="required|integer|min:7|max:11"
                onpaste="return false;"
                :is-allow-special-characters="false"
                readonly
              />
            </b-col>
            <!-- Phone number -->

            <!-- Access Level -->
            <b-col md="6">
              <SelectField
                id="access-level"
                v-model="accountOfficerFormData.accessLevel"
                :label="$t('Access Level')"
                :placeholder="$t('Select')"
                :options="accessLevelOptions"
                :reduce="option => option.value"
                :error-name="$t('Access Level')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!--Access Level -->

            <!--Gender -->
            <b-col md="6">
              <SelectField
                id="gender"
                v-model="accountOfficerFormData.gender"
                :label="$t('Gender')"
                :placeholder="$t('Select')"
                :options="genderOptions"
                :reduce="option => option.value"
                :error-name="$t('Gender')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Gender -->
          </b-row>
          <b-row class="match-height">
            <b-col md="12">
              <TextAreaField
                id="address"
                v-model="accountOfficerFormData.address"
                :label="$t('Address')"
                :placeholder="$t('Enter address')"
                :error-name="$t('Address')"
                rules="required"
                for="address"
                rows="4"
                readonly
              />
            </b-col>
          </b-row>
          <b-row>
            <!-- Supervisor Name -->
            <b-col md="6">
              <SelectField
                id="select-user"
                v-model="accountOfficerFormData.supervisor"
                :label="$t('Supervisor Name')"
                :options="userOptions"
                :placeholder="$t('Select')"
                :reduce="option => option.value"
                item="label"
                rules="required"
                :error-name="$t('Supervisor Name')"
                :loading="isLoading"
                :readonly="isReadonly"
                :set-selected="setSupervisorDetails"
              />
            </b-col>
            <!-- Supervisor Name -->
            <b-col md="6">
              <InputField
                id="supervisor-email"
                v-model="supervisorEmail"
                :label="$t('Supervisor Email')"
                :placeholder="$t('Enter Supervisor Email')"
                type="text"
                :error-name="$t('Supervisor Email')"
                rules="required"
                readonly
              />
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{}"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchAccountOfficer"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Account Officer Name -->
        <b-col md="6">
          <InputField
            id="account-officer-name"
            v-model="filter.name"
            :label="$t('Account Officer Name')"
            :placeholder="$t('Enter account officer name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Account Officer Name -->

        <!-- Staff ID -->
        <b-col md="6">
          <InputField
            id="staff-id"
            v-model="filter.staffID"
            :label="$t('Staff ID')"
            :placeholder="$t('Enter staff ID')"
            type="text"
          />
        </b-col>
        <!-- Staff ID -->

        <!-- Gender -->
        <b-col md="6">
          <SelectField
            id="gender"
            v-model="filter.gender"
            :label="$t('Gender')"
            :placeholder="$t('Select Gender')"
            :options="genderOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <!-- Gender -->

        <!-- Phone Number -->
        <b-col md="6">
          <InputField
            id="phone-number"
            v-model="filter.phoneNumber"
            :label="$t('Phone Number')"
            :placeholder="$t('Enter phone number')"
            type="number"
          />
        </b-col>
        <!-- Phone Number -->

        <!-- Branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="filter.branchCode"
            :label="$t('Branch')"
            :placeholder="$t('Select Branch')"
            :options="branchOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <!-- Branch -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            :options="statusOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BSpinner,
  BBadge,
  BModal,
  BForm,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapGetters, mapActions } from 'vuex'
import { required, minPhoneVal, maxPhoneVal } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import TextAreaField from '../../../forms/TextAreaField.vue'
import { gender, accessLevel } from '../../../../helpers/data'
import getStatusColor from '../../../../helpers/getStatusColor'
import Button from '../../../components/button/Button.vue'
// import { checkTokenValidity } from '@/helpers/token'
import { toDateWithYear } from '../../../../helpers/formatDate'
import { accountPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BDropdown,
    BDropdownItem,
    BBadge,
    TablePagination,
    BModal,
    BForm,
    BSpinner,
    TableEntries,
    SelectField,
    ValidationObserver,
    TextAreaField,
    InputField,
    Button,
    FullScreenSpinner,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      isReadonly: false,
      isEditable: false,
      isFullName: false,
      showSpinner: false,
      sortBy: 'name',
      required,
      isLoading: false,
      minPhoneVal,
      maxPhoneVal,
      modalTitle: 'Create Account Officer',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'staffID', sortable: true, label: 'Staff ID' },
        { key: 'name', sortable: true, label: 'Name' },
        { key: 'phoneNumber', sortable: true, label: 'Phone Number' },
        { key: 'accessLevel', sortable: true, label: 'Access Level' },
        { key: 'branchName', sortable: true, label: 'Branch Name' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      getStatusColor,
      buttonLabel: 'Save',
      accountOfficerFormData: {
        linkedUser: '',
        lastName: '',
        otherNames: '',
        staffID: '',
        phoneNumber: '',
        accessLevel: null,
        gender: null,
        address: '',
        branchCode: '',
        supervisor: '',
        id: '',
      },
      supervisorEmail: '',
      filter: {
        name: '',
        staffID: '',
        gender: '',
        phoneNumber: '',
        branchCode: '',
        status: '',
        sortColumn: '',
      },
      // accessLevelOptions: accessLevel,
      accountOfficerList: [],
      userOptions: [],
      superviorsOptions: [],
      statusOptions: [
        { label: this.$t('Active'), value: 'Active' },
        { label: this.$t('Inactive'), value: 'Inactive' },
      ],
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
    ...mapGetters('tableCache', ['getTableCache']),
    cacheKey() {
      // Use page + perPage + filter as cacheKey
      return JSON.stringify({ page: this.currentPage, perPage: this.perPage, filter: this.filter })
    },
    cachedAccountOfficerList() {
      return this.getTableCache('account-officer', this.cacheKey)
    },

    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.key,
      }))
    },
    accessLevelOptions() {
      return accessLevel.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    genderOptions() {
      return gender.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.serveCacheAndFetchAccountOfficers()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.serveCacheAndFetchAccountOfficers()
    },
  },
  mounted() {
    this.serveCacheAndFetchAccountOfficers()
    this.fetchUsers()
    this.getAllBranches({ permission: accountPermissions.VIEW_ACCOUNT_OFFICER })
  },
  methods: {
    ...mapActions({
      getAllBranches: 'operations/getAllBranches',
      getAccountOfficer: 'account/getAccountOfficerList',
      updateAccountOfficer: 'account/updateAccountOfficerAction',
      createAccountOfficer: 'account/createAccountOfficerAction',
      updateAccountOfficerStatus: 'account/updateStatus',
      getUsers: 'admin/getUserList',
      getUserByEmail: 'admin/getUserByEmail',
      unlockAccountOfficerAction: 'account/unlockAccountOfficerAction',
    }),
    ...mapActions('tableCache', ['setTableCache', 'markTableCacheStale', 'clearTableCache']),

    async serveCacheAndFetchAccountOfficers() {
      const { cacheKey } = this
      const cache = this.getTableCache('account-officer', cacheKey)

      if (cache && cache.data) {
        this.accountOfficerList = cache.data
        this.totalCount = cache.totalCount
        this.isBusy = false
        this.fetchAccountOfficer(true)
      } else {
        // No cache, show spinner and fetch
        this.isBusy = true
        await this.fetchAccountOfficer(false)
      }
    },
    async fetchUsers() {
      try {
        this.isLoading = true
        const response = await this.getUsers({
          status: 'Active',
          permission: accountPermissions.VIEW_ACCOUNT_OFFICER,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.userOptions = data.data.map(items => ({
            label: `${items.firstname} ${items.lastname}`,
            value: items.email,
          }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isLoading = false
      }
    },
    async setUserDetails(email) {
      try {
        this.isLoading = true
        const response = await this.getUserByEmail({
          email,
          permission: accountPermissions.VIEW_ACCOUNT_OFFICER,
        })
        const { status, data } = await response
        if (status === 200) {
          const dataItem = data.data[0]
          this.accountOfficerFormData = {
            ...this.accountOfficerFormData,
            linkedUser: dataItem.email,
            lastName: dataItem.lastname,
            otherNames: dataItem.firstname,
            phoneNumber: dataItem.phoneNumber,
            accessLevel: dataItem.accessLevel,
            address: dataItem.address,
            branchCode: dataItem.branchCode,
          }
        }
      } catch (error) {
        this.toastAlert({ title: error })
      } finally {
        this.isLoading = false
      }
    },
    async setSupervisorDetails(email) {
      if (email) {
        this.supervisorEmail = email
      }
    },

    async fetchAccountOfficer(isBackground = false) {
      if (!isBackground) {
        this.isBusy = true
        this.hideModal('modal-filter')
      }
      try {
        this.hideModal('modal-filter')
        const response = await this.getAccountOfficer({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: accountPermissions.VIEW_ACCOUNT_OFFICER,
        })

        const { status, data } = response
        if (status === 200 && data.data) {
          // If data is different from cache, update UI
          const cache = this.cachedAccountOfficerList
          const isDifferent = !cache || JSON.stringify(cache.data) !== JSON.stringify(data.data)
          if (isDifferent) {
            this.accountOfficerList = data.data
            this.totalCount = data.recordCount
            // If on page 1 and data changed, mark all account officer cache as stale
            if (this.currentPage === 1) {
              this.markTableCacheStale('account-officer')
            }
          }
          // Always update cache
          this.setTableCache({
            tableKey: 'account-officer',
            cacheKey: this.cacheKey,
            payload: {
              data: data.data,
              totalCount: data.recordCount,
              lastFetched: Date.now(),
              stale: false,
            },
          })
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        if (!isBackground) this.isBusy = false
      }
    },
    accountOfficerStatus(items) {
      const { id, name } = items
      const actionType = items.status === 'Active' ? 'InActive' : 'Active'
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active'
        ? accountPermissions.DEACTIVATE_ACCOUNT_OFFICER
        : accountPermissions.ACTIVATE_ACCOUNT_OFFICER

      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            name,
          }
          const response = await this.updateAccountOfficerStatus({
            statusValues,
            actionType,
            routeName: 'AccountOfficer',
            permission,
          })
          if (response.status === 200) {
            this.markTableCacheStale('account-officer')
            this.fetchAccountOfficer(true)
            const successMessage = action === this.$t('Activate')
              ? this.$t('Your request to Activate Account Officer was successful')
              : this.$t('Your request to Deactivate Account Officer was successful')
            this.alertSuccess({ text: successMessage })
            this.showSpinner = false
          }
        } catch (error) {
          this.alertError({
            text:
              action === this.$t('Activate')
                ? this.$t(`Your request to Activate Account Officer failed. Reason: ${error}`)
                : this.$t(`Your request to Deactivate Account Officer failed. Reason: ${error}`),
          })
        } finally {
          this.showSpinner = false
        }
      }

      const actionText = action === this.$t('Activate')
        ? this.$t('Are you sure you want to Activate this Account Officer?')
        : this.$t('Are you sure you want to Deactivate this Account Officer?')
      this.confirm(updateStatus, { text: actionText })
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.accountOfficerRules.validate().then(success => {
        if (success) {
          const actionText = this.buttonLabel === this.$t('Save')
            ? this.$t('Are you sure you want to Save this Account Officer?')
            : this.$t('Are you sure you want to Update this Account Officer?')
          this.confirm(this.accountOfficerAction, { text: actionText })
        }
      })
    },
    async accountOfficerAction() {
      try {
        this.isLoading = true
        delete this.accountOfficerFormData.name
        const { lastName, otherNames } = this.accountOfficerFormData
        const formData = {
          ...this.accountOfficerFormData,
          staffName: `${lastName} ${otherNames}`,
        }
        const response = this.buttonLabel === 'Save'
          ? await this.createAccountOfficer(formData)
          : await this.updateAccountOfficer({
            ...this.accountOfficerFormData,
            staffName: `${lastName} ${otherNames}`,
            branchCode: this.branchOptions.find(
              item => item.label === this.accountOfficerFormData.branchCode
                    || item.value === this.accountOfficerFormData.branchCode,
            ).value,
          })
        const { status } = response
        if (status === 200) {
          this.markTableCacheStale('account-officer')
          this.fetchAccountOfficer(true)
          this.hideModal('modal-lg')
          const successMessage = this.buttonLabel === this.$t('Save')
            ? this.$t('Your request to Save Account Officer was successful')
            : this.$t('Your request to Update Account Officer was successful')
          this.alertSuccess({ text: successMessage })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    async onSelectAccountOfficerItem(items, activity, val) {
      const [lastName, otherNames] = items.name.split(' ')
      this.supervisorEmail = items.supervisor
      this.accountOfficerFormData = { ...items, lastName, otherNames }
      this.isFullName = val !== 'edit'
      this.isEditable = true
      this.isReadonly = val === 'edit' ? (this.isReadonly = false) : (this.isReadonly = true)
      this.modalTitle = val === 'edit' ? this.$t('Edit Account Officer') : this.$t('View Account Officer')
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    async unlockAccountOfficer(items) {
      try {
        this.isLoading = true
        const formData = items
        const response = await this.unlockAccountOfficerAction({
          formData,
          permission: accountPermissions.UNLINK_ACCOUNT_OFFICER,
        })
        const { status } = response
        if (status === 200) {
          this.fetchAccountOfficer(true)
          this.alertSuccess({
            text: this.$t('Your request to Unlink Account Officer was successful'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    resetModal() {
      this.accountOfficerFormData = { ...null }
      this.isLoading = false
      this.isEditable = false
      this.isReadonly = false
      this.buttonLabel = this.$t('Save')
      this.modalTitle = this.$t('Create Account Officer')
    },
    resetFilters() {
      this.filter = {}
      this.markTableCacheStale('account-officer')
    },
    resetFilter() {
      this.filter = {}
      this.markTableCacheStale('account-officer') // Mark all cache as stale on filter reset
      this.fetchAccountOfficer(true)
    },
  },
}
</script>
